/**
 * ATR (Average True Range) Service
 * Calculates ATR-14 from 1-hour candles for dynamic TP/SL calculation
 *
 * Features:
 * - Fetches 1h candles from Hyperliquid API
 * - Calculates ATR-14 (14-period Average True Range)
 * - Caches results with 1-hour TTL (ATR doesn't change mid-candle)
 * - Provides ATR% for volatility-relative calculations
 */

export class ATRService {
    constructor() {
        // Cache: asset -> { atr, atrPct, price, timestamp }
        this.cache = new Map();

        // Cache TTL: 1 hour (ATR based on 1h candles)
        this.TTL = 60 * 60 * 1000;

        // ATR period (standard is 14)
        this.period = 14;

        // TP/SL multipliers
        this.tpMultiplier = 2.0;  // TP = 2x ATR
        this.slMultiplier = 1.5;  // SL = 1.5x ATR

        // Loading states to prevent duplicate requests
        this.loading = new Map();

        // console.log('[ATR-SERVICE] ✅ Initialized');
    }

    /**
     * Get ATR data for an asset
     * Returns: { atr, atrPct, price, timestamp } or null if unavailable
     */
    async getATR(asset) {
        if (!asset) return null;

        // Check cache first
        const cached = this.cache.get(asset);
        if (cached && (Date.now() - cached.timestamp) < this.TTL) {
            return cached;
        }

        // Prevent duplicate requests
        if (this.loading.get(asset)) {
            // Wait for existing request
            await new Promise(resolve => setTimeout(resolve, 100));
            return this.cache.get(asset) || null;
        }

        this.loading.set(asset, true);

        try {
            // Fetch 1h candles (need 15 for ATR-14 calculation)
            const endTime = Date.now();
            const startTime = endTime - (20 * 60 * 60 * 1000); // 20 hours back (buffer)

            const candles = await window.BrowserHyperliquidClient.getCandleData(
                asset,
                '1h',
                startTime,
                endTime
            );

            if (!candles || candles.length < this.period) {
                // console.warn(`[ATR-SERVICE] Insufficient candles for ${asset}: ${candles?.length || 0}`);
                return null;
            }

            // Calculate ATR
            const atr = this.calculateATR(candles);

            // Get current price from WebSocket or last candle
            let currentPrice = window.BrowserPriceService?.getPrice(asset);
            if (!currentPrice) {
                const lastCandle = candles[candles.length - 1];
                currentPrice = parseFloat(lastCandle.c || lastCandle.close);
            } else {
                currentPrice = parseFloat(currentPrice);
            }

            // Calculate ATR as percentage of price
            const atrPct = (atr / currentPrice) * 100;

            // Cache the result
            const result = {
                atr,
                atrPct,
                price: currentPrice,
                timestamp: Date.now()
            };

            this.cache.set(asset, result);

            // console.log(`[ATR-SERVICE] ${asset}: ATR=${atr.toFixed(4)}, ATR%=${atrPct.toFixed(2)}%, Price=${currentPrice}`);

            return result;

        } catch (error) {
            // console.error(`[ATR-SERVICE] Error fetching ATR for ${asset}:`, error);
            return null;
        } finally {
            this.loading.set(asset, false);
        }
    }

    /**
     * Calculate ATR (Average True Range)
     * True Range = max(H-L, |H-prevC|, |L-prevC|)
     * ATR = SMA of True Range over period
     */
    calculateATR(candles, period = this.period) {
        if (candles.length < period) {
            return 0;
        }

        const trueRanges = [];

        for (let i = 0; i < candles.length; i++) {
            const candle = candles[i];
            const high = parseFloat(candle.h || candle.high);
            const low = parseFloat(candle.l || candle.low);
            const close = parseFloat(candle.c || candle.close);

            if (i === 0) {
                // First candle: TR = H - L
                trueRanges.push(high - low);
            } else {
                const prevClose = parseFloat(candles[i - 1].c || candles[i - 1].close);
                // TR = max(H-L, |H-prevC|, |L-prevC|)
                const tr = Math.max(
                    high - low,
                    Math.abs(high - prevClose),
                    Math.abs(low - prevClose)
                );
                trueRanges.push(tr);
            }
        }

        // Calculate Simple Moving Average of True Ranges (last 'period' values)
        const recentTR = trueRanges.slice(-period);
        const atr = recentTR.reduce((sum, tr) => sum + tr, 0) / period;

        return atr;
    }

    /**
     * Calculate TP price based on ATR
     * LONG: entry + (ATR * multiplier)
     * SHORT: entry - (ATR * multiplier)
     */
    calculateTP(entryPrice, atr, isLong) {
        const offset = atr * this.tpMultiplier;
        return isLong ? entryPrice + offset : entryPrice - offset;
    }

    /**
     * Calculate SL price based on ATR
     * LONG: entry - (ATR * multiplier)
     * SHORT: entry + (ATR * multiplier)
     */
    calculateSL(entryPrice, atr, isLong) {
        const offset = atr * this.slMultiplier;
        return isLong ? entryPrice - offset : entryPrice + offset;
    }

    /**
     * Get TP and SL prices for a position/order
     * Convenience method that fetches ATR and calculates both
     */
    async getTPSL(asset, entryPrice, isLong) {
        const atrData = await this.getATR(asset);

        if (!atrData || !atrData.atr) {
            // Fallback to fixed 2% if ATR unavailable
            // console.warn(`[ATR-SERVICE] Using fallback 2% for ${asset}`);
            const fallbackPct = 0.02;
            return {
                tp: isLong ? entryPrice * (1 + fallbackPct) : entryPrice * (1 - fallbackPct),
                sl: isLong ? entryPrice * (1 - fallbackPct) : entryPrice * (1 + fallbackPct),
                atr: null,
                atrPct: null,
                isFallback: true
            };
        }

        const { atr, atrPct } = atrData;

        return {
            tp: this.calculateTP(entryPrice, atr, isLong),
            sl: this.calculateSL(entryPrice, atr, isLong),
            atr,
            atrPct,
            isFallback: false
        };
    }

    /**
     * Round price to appropriate decimals based on price magnitude
     */
    roundPrice(price, referencePrice = null) {
        const ref = referencePrice || price;
        let decimals;

        if (ref >= 10000) decimals = 1;      // BTC-like
        else if (ref >= 1000) decimals = 2;   // ETH-like
        else if (ref >= 100) decimals = 3;    // SOL-like
        else if (ref >= 10) decimals = 4;     // Mid-cap
        else if (ref >= 1) decimals = 5;      // Low-cap
        else decimals = 6;                     // Micro-cap

        return parseFloat(price.toFixed(decimals));
    }

    /**
     * Get TP and SL with proper price rounding
     */
    async getTPSLRounded(asset, entryPrice, isLong) {
        const result = await this.getTPSL(asset, entryPrice, isLong);

        return {
            ...result,
            tp: this.roundPrice(result.tp, entryPrice),
            sl: this.roundPrice(result.sl, entryPrice)
        };
    }

    /**
     * Get cached ATR data synchronously (no API call)
     * Returns cached data if available and not expired, null otherwise
     */
    getCachedATR(asset) {
        const cached = this.cache.get(asset);
        if (cached && (Date.now() - cached.timestamp) < this.TTL) {
            return cached;
        }
        return null;
    }

    /**
     * Get TP/SL synchronously from cache, with fallback to fixed percentage
     * Use this for synchronous rendering (e.g., positions list)
     */
    getTPSLSync(asset, entryPrice, isLong) {
        // console.log(`[ATR-SERVICE] getTPSLSync called: asset=${asset}, entry=${entryPrice}, isLong=${isLong}`);
        const cached = this.getCachedATR(asset);

        if (cached && cached.atr) {
            const tp = this.roundPrice(this.calculateTP(entryPrice, cached.atr, isLong), entryPrice);
            const sl = this.roundPrice(this.calculateSL(entryPrice, cached.atr, isLong), entryPrice);
            // console.log(`[ATR-SERVICE] ✅ ATR cached - TP=${tp}, SL=${sl}, ATR=${cached.atr.toFixed(2)} (${cached.atrPct.toFixed(2)}%)`);
            return { tp, sl, atr: cached.atr, atrPct: cached.atrPct, isFallback: false };
        }

        // Fallback to 2% if ATR not cached
        // console.log(`[ATR-SERVICE] ⚠️ No ATR cached for ${asset}, using ±2% fallback`);
        const fallbackPct = 0.02;
        const tp = isLong ? entryPrice * (1 + fallbackPct) : entryPrice * (1 - fallbackPct);
        const sl = isLong ? entryPrice * (1 - fallbackPct) : entryPrice * (1 + fallbackPct);
        const result = {
            tp: this.roundPrice(tp, entryPrice),
            sl: this.roundPrice(sl, entryPrice),
            atr: null,
            atrPct: null,
            isFallback: true
        };
        // console.log(`[ATR-SERVICE] Fallback result - TP=${result.tp}, SL=${result.sl}`);
        return result;
    }

    /**
     * Invalidate cache for an asset (force refresh on next request)
     */
    invalidateCache(asset) {
        if (asset) {
            this.cache.delete(asset);
        } else {
            this.cache.clear();
        }
    }

    /**
     * Update multipliers
     */
    setMultipliers(tpMultiplier, slMultiplier) {
        if (tpMultiplier > 0) this.tpMultiplier = tpMultiplier;
        if (slMultiplier > 0) this.slMultiplier = slMultiplier;
        // console.log(`[ATR-SERVICE] Multipliers updated: TP=${this.tpMultiplier}x, SL=${this.slMultiplier}x`);
    }

    /**
     * Get cache stats
     */
    getStats() {
        const stats = {
            cachedAssets: this.cache.size,
            entries: []
        };

        for (const [asset, data] of this.cache.entries()) {
            stats.entries.push({
                asset,
                atr: data.atr,
                atrPct: data.atrPct,
                age: Math.round((Date.now() - data.timestamp) / 60000) + ' min'
            });
        }

        return stats;
    }
}

// Create and expose singleton instance
if (typeof window !== 'undefined') {
    window.ATRService = new ATRService();
    // console.log('[ATR-SERVICE] ✅ Singleton initialized on window');
}
